import Metatags from '@components/common/Metatags';
import StoreDetails from '@components/storelocator/StoreDetails';
import StoreLocatorMap from '@components/storelocator/StoreLocatorMap';
import StoreLocatorSearch from '@components/storelocator/StoreLocatorSearch';
import { StoreLocatorActionTypes } from '@state/features/common.types';
import { currentStoreSelector } from '@state/features/storelocator.selectors';
import analytics from '@utils/analytics';
import { tranitionDomReady } from '@utils/landingAnimations';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import StoreBreadcrumb from './StoreBreadcrumb';
import { autoChooseClassnames } from '@common/utils/brandDiffsUtils';
const COLLECTIONS = ['men', 'women', 'children'];
const COLLECTIONSCODES = ['01', '02', '03'];
const StoreLocator = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { country, city } = useParams();
    const { stores, storesInMap, filters, zoom, center, updateBounds, currentLocation, address, metadata, openClusterDetail } = useSelector((state) => state.storelocator);
    const currentStore = useSelector(currentStoreSelector);
    const [searchedCity, setSearchedCity] = useState();
    const [searchedCountry, setSearchedCountry] = useState();
    const [filteredStores, setFilteredStores] = useState(stores);
    const [showLeftBar, setShowLeftBar] = useState(false);
    useEffect(() => {
        if (openClusterDetail || currentStore) {
            setShowLeftBar(true);
        }
        else {
            setShowLeftBar(false);
        }
    }, [currentStore, openClusterDetail]);
    useEffect(() => {
        dispatch({
            type: StoreLocatorActionTypes.FETCH_STORES,
            payload: {
                city,
                country,
            },
        });
    }, []);
    useEffect(() => {
        if (storesInMap) {
            if (storesInMap.length > 0) {
                // filtro i servizi
                const filteredServices = storesInMap.filter((el) => {
                    let filtersMatch = true;
                    const storeServices = Object.assign({}, el.storeServices);
                    filters.services.forEach((service) => {
                        if (!storeServices[service]) {
                            filtersMatch = false;
                        }
                    });
                    return filtersMatch;
                });
                // filtro le collections
                const filteredCollections = filteredServices.filter((el) => {
                    var _a;
                    let filtersMatch = true;
                    if (filters.collections.length > 0) {
                        const collections = (_a = el.storeCollections) === null || _a === void 0 ? void 0 : _a.toLowerCase().split(' ');
                        if (collections && collections.length) {
                            filters.collections.forEach((col) => {
                                // added fallback for filters
                                if (!collections.includes(col) && !collections.includes(t(`storelocator.${COLLECTIONS[COLLECTIONSCODES.indexOf(col)]}`).toLowerCase())) {
                                    filtersMatch = false;
                                }
                            });
                        }
                        else {
                            filtersMatch = false;
                        }
                    }
                    return filtersMatch;
                });
                // filtro country & city
                let filteredCityCountry = filteredCollections;
                if (searchedCity) {
                    filteredCityCountry = filteredCollections.filter((store) => store.city === searchedCity);
                }
                else if (searchedCountry) {
                    //Filtro custom x OE-9856 - Province of China
                    if (searchedCountry == 'CN') {
                        filteredCityCountry = filteredCollections.filter((store) => store.countryCode.value === 'CN' || store.countryCode.value === 'MO' || store.countryCode.value === 'HK' || store.countryCode.value === 'TW');
                    }
                    else {
                        filteredCityCountry = filteredCollections.filter((store) => store.countryCode.value === searchedCountry);
                    }
                }
                setFilteredStores(filteredCityCountry);
            }
            else {
                analytics.storelocatorSearchNotFound(address);
                // console.info('stores: ' + storesInMap);
                setFilteredStores(storesInMap);
            }
        }
    }, [storesInMap, filters, searchedCity, searchedCountry]);
    const escapedCity = useMemo(() => {
        var _a;
        return (_a = city === null || city === void 0 ? void 0 : city.split('-')) === null || _a === void 0 ? void 0 : _a.join(' ');
    }, [city]);
    const escapedCountry = useMemo(() => {
        var _a;
        return (_a = country === null || country === void 0 ? void 0 : country.split('-')) === null || _a === void 0 ? void 0 : _a.join(' ');
    }, [country]);
    const storeLocatorTitle = useCallback(() => {
        let title = t('store.title');
        if (city || country) {
            title = `${t('store.localizedTitle')} ${escapedCity || escapedCountry}`;
        }
        return (React.createElement(motion.h1, { transition: tranitionDomReady, initial: { opacity: 0 }, animate: { opacity: 1 }, className: "editorial-page-title" }, title));
    }, []);
    return (React.createElement(React.Fragment, null,
        metadata && React.createElement(Metatags, { pageMetaTags: metadata.pageMetaTags, meta: metadata }),
        filteredStores && (React.createElement("div", { className: "store-locator" },
            storeLocatorTitle(),
            React.createElement("div", { className: "store-locator__wrapper" },
                !showLeftBar && (React.createElement(StoreLocatorSearch, { filteredStores: filteredStores, initialSearch: escapedCity || escapedCountry, searchedCity: (e) => setSearchedCity(e), searchedCountry: (e) => setSearchedCountry(e) })),
                React.createElement("div", { className: "row row--full-bleed" },
                    showLeftBar && (React.createElement("div", { className: autoChooseClassnames({
                            MO: 'col-12 col-sm-6 col-md-5 col-lg-4',
                            ST: 'col-24 col-lg-5',
                        }) },
                        React.createElement(StoreDetails, Object.assign({ store: currentStore, filteredStores: filteredStores }, props)))),
                    React.createElement("div", { className: showLeftBar
                            ? autoChooseClassnames({
                                /* eslint-disable  prettier/prettier */
                                MO: 'col-12 col-sm-6 col-md-7 col-lg-8',
                                ST: 'col-24 col-lg-19',
                            })
                            : autoChooseClassnames({
                                /* eslint-disable  prettier/prettier */
                                MO: 'col-12',
                                ST: 'col-24',
                            }) },
                        React.createElement(StoreLocatorMap, Object.assign({}, props, { zoom: zoom, center: center, updatedMapBounds: updateBounds, updateFromLocation: currentLocation, stores: stores, filteredStores: filteredStores, currentStore: currentStore }))))),
            (city || country) && stores.length > 0 && (React.createElement("div", { className: "container" },
                React.createElement(StoreBreadcrumb, { store: stores[0], currentCity: city, currentCountry: country, inStoreLocator: true })))))));
};
export default StoreLocator;
